import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { STATUS_META } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Package, Store, Truck } from "lucide-react";

export default function MyOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/mine").then((res) => setOrders(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const active = orders.filter((o) => ["pending", "confirmed", "ready"].includes(o.status));
  const past = orders.filter((o) => ["collected", "cancelled"].includes(o.status));

  const render = (list) => list.length === 0 ? (
    <Card className="p-10 text-center text-muted-foreground rounded-2xl">No orders here.</Card>
  ) : (
    <div className="space-y-3">
      {list.map((o) => (
        <Card key={o.id} className="rounded-2xl p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{o.farm_name}</span>
                <Badge className={STATUS_META[o.status]?.color}>{STATUS_META[o.status]?.label}</Badge>
                <Badge variant="secondary">{o.fulfilment_type === "delivery" ? <Truck className="h-3 w-3 mr-1" /> : <Store className="h-3 w-3 mr-1" />}{o.fulfilment_type}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Order #{o.id.slice(0, 8)} · {o.fulfilment_date ? new Date(o.fulfilment_date).toLocaleDateString("en-GB") : "—"}</p>
              {o.fulfilment_type === "delivery" && o.delivery_address && (
                <p className="text-xs text-muted-foreground">Deliver to: {o.delivery_address}</p>
              )}
              <ul className="text-sm mt-2 space-y-0.5">
                {o.items.map((it) => <li key={it.id}>{it.quantity} × {it.product_name}</li>)}
              </ul>
            </div>
            <div className="text-right">
              <div className="font-semibold text-lg">£{o.total_amount.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">Pay on {o.fulfilment_type}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  if (loading) return <div className="pt-32 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="pt-16 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-5"><Package className="h-5 w-5 text-primary" /><h1 className="text-2xl font-semibold">My Orders</h1></div>
        {orders.length === 0 ? (
          <Card className="p-10 text-center rounded-2xl">
            <p className="text-muted-foreground">You haven't placed any orders yet.</p>
            <Button className="mt-4" onClick={() => navigate("/map")}>Find local farms</Button>
          </Card>
        ) : (
          <Tabs defaultValue="active" data-testid="my-orders-list">
            <TabsList><TabsTrigger value="active">Active ({active.length})</TabsTrigger><TabsTrigger value="past">Past ({past.length})</TabsTrigger></TabsList>
            <TabsContent value="active" className="mt-4">{render(active)}</TabsContent>
            <TabsContent value="past" className="mt-4">{render(past)}</TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
